/*
 * Free delivery progress bar (snippets/free-delivery-bar.liquid).
 * Liquid renders the first state and resolves the customer's {type, threshold};
 * this element re-renders from /cart.js after every cart change, without a reload.
 *
 * Cart change sources:
 * 1. Hyper's FoxTheme.pubsub "cart-update" event (product form, cart drawer, cart page, bundles,
 *    quick order list, gift wrapping) and the "cart:updated" document event.
 * 2. Fallback: any fetch or XHR request to /cart/add, /cart/change, /cart/update or /cart/clear
 *    (apps, custom scripts that bypass the theme).
 */
(() => {
  if (customElements.get('free-delivery-bar')) return;

  const CART_MUTATION = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
  const bars = new Set();
  let timer = null;
  let request = 0;

  const money = (pence) =>
    new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(pence / 100);
  const moneyShort = (pence) =>
    pence % 100 === 0 ? money(pence).replace(/\.00$/, '') : money(pence);

  async function refresh() {
    if (!bars.size) return;
    const current = ++request;
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const response = await fetch(root + 'cart.js', {
        headers: { Accept: 'application/json' },
        cache: 'no-store',
      });
      const cart = await response.json();
      if (current !== request) return; // a newer refresh is on its way
      bars.forEach((bar) => bar.render(cart));
    } catch (error) {
      // keep the last rendered state
    }
  }

  function scheduleRefresh() {
    clearTimeout(timer);
    timer = setTimeout(refresh, 150);
  }

  function subscribeToTheme() {
    const pubsub = window.FoxTheme && window.FoxTheme.pubsub;
    if (!pubsub || !pubsub.PUB_SUB_EVENTS) return false;
    pubsub.subscribe(pubsub.PUB_SUB_EVENTS.cartUpdate, scheduleRefresh);
    return true;
  }
  if (!subscribeToTheme()) document.addEventListener('DOMContentLoaded', subscribeToTheme, { once: true });
  document.addEventListener('cart:updated', scheduleRefresh);
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) scheduleRefresh();
  });

  const nativeFetch = window.fetch;
  window.fetch = function (...args) {
    const url = String((args[0] && args[0].url) || args[0] || '');
    const promise = nativeFetch.apply(this, args);
    if (CART_MUTATION.test(url)) promise.then(scheduleRefresh, scheduleRefresh);
    return promise;
  };

  const nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    if (CART_MUTATION.test(String(url))) this.addEventListener('loadend', scheduleRefresh);
    return nativeOpen.call(this, method, url, ...rest);
  };

  class FreeDeliveryBar extends HTMLElement {
    connectedCallback() {
      try {
        this.config = JSON.parse(this.querySelector('[data-fdb-config]').textContent);
        this.text = JSON.parse(this.querySelector('[data-fdb-text]').textContent);
      } catch (error) {
        this.hidden = true; // no config, no bar: never show a wrong amount
        return;
      }
      if (this.config.warning) console.warn('[free-delivery-bar] ' + this.config.warning);

      this.productId = Number(this.dataset.productId);
      this.msg = this.querySelector('[data-fdb-msg]');
      this.total = this.querySelector('[data-fdb-total]');
      this.track = this.querySelector('[data-fdb-track]');
      this.fill = this.querySelector('[data-fdb-fill]');
      this.foot = this.querySelector('[data-fdb-foot]');
      bars.add(this);
    }

    disconnectedCallback() {
      bars.delete(this);
    }

    boxes(count) {
      return count + ' ' + (count === 1 ? this.text.boxSingular : this.text.boxPlural);
    }

    render(cart) {
      if (cart.currency && cart.currency !== 'GBP') {
        this.hidden = true;
        return;
      }
      this.hidden = false;

      const { type, threshold, taxesIncluded, vat, excluded = [] } = this.config;
      const exVat = (pence) => (taxesIncluded ? Math.floor((pence * 100) / vat) : pence);

      // Subtotal before discounts: the checkout shipping rule ignores discounts
      let total = cart.original_total_price;
      let productTotal = 0;
      let boxes = 0;
      cart.items.forEach((item) => {
        if (excluded.includes(String(item.variant_id))) {
          total -= item.original_line_price;
          return;
        }
        boxes += item.quantity;
        if (item.product_id === this.productId) productTotal += item.original_line_price;
      });
      total = exVat(Math.max(0, total));
      productTotal = exVat(productTotal);
      const other = Math.max(0, total - productTotal);

      const current = type === 'box' ? boxes : total;
      const remaining = Math.max(0, threshold - current);
      const qualified = remaining === 0;
      const pct = threshold > 0 ? Math.min(100, (current / threshold) * 100) : 100;

      const tokens =
        type === 'box'
          ? { remaining: this.boxes(remaining), threshold: this.boxes(threshold) }
          : { remaining: money(remaining), threshold: moneyShort(threshold) };
      Object.assign(tokens, { subtotal: money(total), other: money(other), count: this.boxes(boxes) });
      const fill = (template) =>
        String(template || '').replace(/\[(remaining|threshold|subtotal|other|count)\]/g, (_, key) => tokens[key]);

      let foot = qualified
        ? this.text.footQualified
        : type === 'box'
          ? this.text.footBox
          : this.text.footValue;
      if (type === 'box') {
        if (boxes > 0) foot += ' ' + this.text.basketBox;
      } else if (total > 0) {
        foot += ' ' + (other > 0 ? this.text.basketValueOther : this.text.basketValueOnly);
      }

      const message = fill(qualified ? this.text.msgQualified : this.text.msgUnder);
      this.classList.toggle('is-qualified', qualified);
      if (this.msg.textContent !== message) this.msg.textContent = message;
      this.total.textContent =
        type === 'box' ? boxes + ' / ' + tokens.threshold : money(total) + ' / ' + tokens.threshold;
      this.total.hidden = type !== 'box' && threshold === 0;
      this.fill.style.width = pct + '%';
      this.track.setAttribute('aria-valuemax', type === 'box' ? threshold : threshold / 100);
      this.track.setAttribute(
        'aria-valuenow',
        type === 'box' ? Math.min(current, threshold) : Math.min(current, threshold) / 100
      );
      this.track.setAttribute('aria-valuetext', message);
      this.foot.textContent = fill(foot).trim();
    }
  }

  customElements.define('free-delivery-bar', FreeDeliveryBar);
})();
